import React, { useState } from "react";
import { Box, Grid2, Typography } from "@mui/material";
import SOHAndRULKPISection from "./SOHAndRULKPISection";
import StateOfHealthChart from "./StateOfHealthChart";
import StateOfHealthBreakdownChart from "./StateOfHealthBreakdownChart";
import SOHBucketMigrationChart, { DEFAULT_SOH_MIGRATION_TITLE } from "./SOHBucketMigrationChart";
import CartLoader from "../../../Helper/Loaders/CartLoader";
import { useBaseEntityInfo, usePagesContent } from "../../../Hooks/usePagesContent";
import SOHDetailsView from "./SOHDetails/SOHDetailsView";
import RemainingUsefulLifeChart from "./RemainingUsefulLifeChart";
import RULDetailsView from "./RULDetails/RULDetailsView";
import RemainingUsefulLifeBreakdownChart from "./RemainingUsefulLifeBreakdownChart";
import RULWarrantyOverview from "./RULWarrantyOverview";
import OrgNotificationBanner from "../../../Components/OrgNotificationBanner/OrgNotificationBanner";
import { useCellChemistryOptions } from "./utils/cellChemistryFilter";

const SOHAndRUL = ({
  vehiclesConnected,
  isLoadingVehiclesCount,
  globalFilters = {},
  allOnboardingLocationsHierarchy = [],
  allFleets = [],
  showSOHDetails = false,
  onShowSOHDetails,
  showRULDetails = false,
  onShowRULDetails,
  waitForOperationalStatus = false,
}) => {
  const {
    baseEntityTypePluralLable,
  } = useBaseEntityInfo();

  const pageContent = usePagesContent();
  const sohAndRULConfig = pageContent?.metaInfo?.BatteryIntelligence?.SOHAndRUL || {};
  const charts = sohAndRULConfig.charts || {};
  const supportedDimensions = sohAndRULConfig.supportedDimensions || {};
  const sohMigrationTitle =
    typeof charts.sohMigrationSankeyChart?.title === "string" && charts.sohMigrationSankeyChart.title.trim()
      ? charts.sohMigrationSankeyChart.title
      : DEFAULT_SOH_MIGRATION_TITLE;

  // Fetched once for both cards. Each card keeps its own selection, held here so it
  // survives a trip to the Details view and back (the cards remount on return).
  const cellChemistryOptions = useCellChemistryOptions();
  const [sohCellChemistry, setSohCellChemistry] = useState(null);
  const [rulCellChemistry, setRulCellChemistry] = useState(null);

  if (showSOHDetails) {
    return (
      <SOHDetailsView
        globalFilters={globalFilters}
        onBack={() => onShowSOHDetails(false)}
        allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
        allFleets={allFleets}
      />
    );
  }

  if (showRULDetails) {
    return (
      <RULDetailsView
        globalFilters={globalFilters}
        chartConfig={charts}
        onBack={() => onShowRULDetails(false)}
        allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
        allFleets={allFleets}
      />
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, maxWidth: "calc(100vw - 102px)" }}>
      <OrgNotificationBanner notifications={sohAndRULConfig.notifications} />
      <Grid2 container alignItems="center" justifyContent="space-between">
        <Box>
          <Typography
            variant="fw400s20c000"
            sx={{
              lineHeight: "28px",
              fontWeight: 300,
            }}
          >
            Key Performance Indicators
          </Typography>
        </Box>
        {isLoadingVehiclesCount ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "16px",
              padding: "8px 16px",
            }}
          >
            <CartLoader />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              padding: "8px 16px",
              borderRadius: "8px",
            }}
          >
            <Typography
              variant="fw400s16c000000"
              sx={{
                color: "#444B51",
                lineHeight: "16px",
              }}
            >
              Connected {baseEntityTypePluralLable} Today :
            </Typography>
            <Typography
              variant="fw400s16c000000"
              sx={{
                fontWeight: 600,
                color: "#2E2E2E",
                lineHeight: "16px",
              }}
            >
              {vehiclesConnected}
            </Typography>
          </Box>
        )}
      </Grid2>

      {/* KPI Section */}
      <SOHAndRULKPISection
        globalFilters={globalFilters}
        vehiclesConnected={vehiclesConnected}
        showHeader={false}
      />

      {/* State of Health (SOH) section */}
      {(charts.sohDonutChart?.enabled === true || charts.sohScatterChart?.enabled === true) && (
        <Box>
          <Typography
            variant="fw400s20c000"
            sx={{ lineHeight: "28px", fontWeight: 300, mb: 2, display: "block" }}
          >
            State of Health (SOH)
          </Typography>
          <StateOfHealthChart
            globalFilters={globalFilters}
            onDetailsClick={() => onShowSOHDetails(true)}
            chartConfig={charts}
            supportedDimensions={supportedDimensions}
            cellChemistryOptions={cellChemistryOptions}
            cellChemistry={sohCellChemistry}
            onCellChemistryChange={setSohCellChemistry}
          />
        </Box>
      )}

      {/* State of Health Breakdown section */}
      {charts.sohBreakdownChart?.enabled === true && (
        <Box>
          <Typography
            variant="fw400s20c000"
            sx={{ lineHeight: "28px", fontWeight: 300, mb: 2, display: "block" }}
          >
            State of Health Breakdown
          </Typography>
          <StateOfHealthBreakdownChart
            globalFilters={globalFilters}
            chartConfig={charts}
          />
        </Box>
      )}

      {/* SOH Bucket Migration section (monthly sankey) */}
      {charts.sohMigrationSankeyChart?.enabled === true && (
        <Box>
          <Typography
            variant="fw400s20c000"
            sx={{ lineHeight: "28px", fontWeight: 300, mb: 2, display: "block" }}
          >
            {sohMigrationTitle}
          </Typography>
          <SOHBucketMigrationChart
            globalFilters={globalFilters}
            title={sohMigrationTitle}
            months={charts.sohMigrationSankeyChart.months}
            levels={charts.sohMigrationSankeyChart.levels}
            buckets={charts.sohMigrationSankeyChart.buckets}
            waitForOperationalStatus={waitForOperationalStatus}
          />
        </Box>
      )}

      {/* Remaining Useful Life (RUL) section */}
      {(charts.rulDonutChart?.enabled === true || charts.rulScatterChart?.enabled === true) && (
        <Box>
          <Typography
            variant="fw400s20c000"
            sx={{ lineHeight: "28px", fontWeight: 300, mb: 2, display: "block" }}
          >
            Remaining Useful Life (RUL)
          </Typography>
          <RemainingUsefulLifeChart
            globalFilters={globalFilters}
            chartConfig={charts}
            onDetailsClick={() => onShowRULDetails(true)}
            cellChemistryOptions={cellChemistryOptions}
            cellChemistry={rulCellChemistry}
            onCellChemistryChange={setRulCellChemistry}
          />
        </Box>
      )}

      {/* Remaining Useful Life Breakdown section */}
      {charts.rulBreakdownChart?.enabled === true && (
        <Box>
          <Typography
            variant="fw400s20c000"
            sx={{ lineHeight: "28px", fontWeight: 300, mb: 2, display: "block" }}
          >
            Remaining Useful Life Breakdown
          </Typography>
          <RemainingUsefulLifeBreakdownChart
            globalFilters={globalFilters}
            chartConfig={charts}
          />
        </Box>
      )}

      {charts.warrantyChart?.enabled === true && (
        <Box>
          <Typography
            variant="fw400s20c000"
            sx={{ lineHeight: "28px", fontWeight: 300, mb: 2, display: "block" }}
          >
            Warranty
          </Typography>
          <RULWarrantyOverview globalFilters={globalFilters} />
        </Box>
      )}
    </Box>
  );
};

export default SOHAndRUL;
